import { AudioMutedOutlined, AudioOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { CustomButton, showToaster, toasterTypes } from "../../widgets";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/slices/auth";
import { FIELD_TYPES, FieldItem } from "../../widgets/fields";
import { useLocation, useNavigate } from "react-router-dom";
import { getFormVariables, getNonSurveyForm, uploadSpeechToTextAudioFiles } from "./apiController";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import { cloneDeep } from "lodash";
import { SurveyFormElements } from "../surveyForm/formElements";
import { languagesData } from "../common/utils/languagesData";
import dayjs from "dayjs";
import { agentNamesDataMap } from "../common/utils/aiAgentsData";

const SpeechToTextDataEntry = () => {
    const location = useLocation();
    const props = location.state || {};

    const [formData, setFormData] = useState<any>(props?.selectedFormData || {});
    const [selectedLanguage, setSelectedLanguage] = useState<any>({ label: "English", value: "en" });
    const [promptMessage, setPromptMessage] = useState<string>("");
    const [isTranslate, setIsTranslate] = useState<any>({ label: "Yes", value: true });
    const [uidFieldIds, setUidFieldIds] = useState<string[]>([]);

    const navigate = useNavigate();

    const FormElements = {
        fieldValue: {
            name: "fieldValue",
            label: "",
            isMandatory: true
        }
    }

    const [values, fields, handleChange, { validateValues, reset, updateValue, updateError, updateAllError, removeErrors }] = useFieldItem(FormElements, {}, { onValueChange });

    const gridFields = ["checkBoxGrid", "radioButtonGrid", "textBoxGrid"];

    // AUDIO STATES
    const [recording, setRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const historyInitializedRef = useRef(false);


    // VARIABLES DATA
    const [variables, setVariables] = useState<any[]>([]);

    const dispatch = useDispatch();

    // Fetch variables from API
    useEffect(() => {
        if (props.selectedFormData?.id) {
            fetchVariables();
        }
    }, [props.selectedFormData?.id]);


    const fetchVariables = async () => {
        try {
            dispatch(setLoading(true));

            const variablesResponse = await getFormVariables(props.selectedFormData?.id);
            const processedFields = processFormFields(variablesResponse.data || []);

            const uidFieldIds = processedFields
                .filter((field: any) =>
                    (field.field_use_type ?? "").includes("UID")
                )
                .map((field: any) => field.id);

            const response = await getNonSurveyForm(props.selectedFormData?.id, true);
            const nonSurveyProcessed = processFormFields(
                response.data.form_fields || []
            );

            const nonSurveyUidMap = new Map(
                nonSurveyProcessed
                    .filter((field: any) => uidFieldIds.includes(field.id))
                    .map((field: any) => [field.id, field])
            );

            const finalUidFields = uidFieldIds.map((id: any) => {
                return (
                    nonSurveyUidMap.get(id) ||
                    processedFields.find((f: any) => f.id === id)
                );
            }).filter(Boolean);

            const nonUidFields = processedFields.filter(
                (field: any) => !uidFieldIds.includes(field.id)
            );

            const finalFields = [...finalUidFields, ...nonUidFields];

            setVariables(finalFields);
            setUidFieldIds(uidFieldIds);

            dispatch(setLoading(false));
        } catch (error: any) {
            console.error("Error fetching variables:", error);
            showToaster(toasterTypes.ERROR, error?.message || "Failed to fetch variables");
            dispatch(setLoading(false));
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm' // or 'audio/wav' depending on backend requirements
            });

            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setAudioBlob(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setRecording(true);
            // showToaster(toasterTypes.SUCCESS, 'Recording started');
        } catch (error) {
            console.error('Error starting recording:', error);
            showToaster(toasterTypes.ERROR, 'Failed to start recording. Please check microphone permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && recording) {
            mediaRecorderRef.current.stop();
            setRecording(false);
            // showToaster(toasterTypes.SUCCESS, 'Recording stopped');
        }
    };

    async function getAudioDuration(audioBlob) {
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        return audioBuffer.duration; // duration in seconds
    }

    const handleSubmit = async () => {
        if (!audioBlob) {
            showToaster(toasterTypes.ERROR, 'Please record audio before submitting');
            return;
        }

        try {
            dispatch(setLoading(true));
            const fileData = audioBlob;
            const token = localStorage.getItem('coguide_token') || '';
            const fileDurationInSeconds = await getAudioDuration(audioBlob);
            const roundedDuration = Math.round(fileDurationInSeconds);
            const aiAgents: any = JSON.parse(localStorage.getItem("aiAgents") || "[]");
            let speechToTextAgent: any;
            const languageCode = selectedLanguage?.value + "-IN" || 'en-IN';
            const formId = props.selectedFormData?.id;
            const is_translate = isTranslate?.value || false;
            const selectedUidFields = variables.filter((field) =>
                uidFieldIds.includes(field.id)
            );

            const userIdentifiers = selectedUidFields.map((field) => {
                if (
                    (field.field_type === "dropDown" ||
                        field.field_type === "dropDownPreviousResponses") &&
                    (field.field_use_type ?? "").includes("UID")
                ) {
                    return {
                        "User_id_field_id": field.id,
                        "User_id": field.field_selected_value?.value ?? ""
                    };
                }

                return {
                    "User_id_field_id": field.id,
                    "User_id": field.field_selected_value ?? ""
                };
            });

            if (is_translate) {
                speechToTextAgent = aiAgents?.find((agent: any) => agent.name === agentNamesDataMap.speech_to_text_translate);
            } else {
                speechToTextAgent = aiAgents?.find((agent: any) => agent.name === agentNamesDataMap.speech_to_text_normal);
            }

            const formData = new FormData();
            formData.append('file', fileData, 'recording.webm');
            formData.append('token', token);
            formData.append('prompt', promptMessage || '');
            formData.append('file_duration', roundedDuration.toString());
            formData.append('module_id', speechToTextAgent?.id || '');
            formData.append('topic_id', props.projectData?.TopicID || '');
            formData.append('language_code', languageCode);
            formData.append('is_conversation', 'false');
            formData.append('target_form_id', formId);
            formData.append('is_translate', is_translate.toString());
            formData.append('User_identifiers', userIdentifiers.length > 0 ? JSON.stringify(userIdentifiers) : '');

            const params = {
                form_id: formId,
            };

            const response = await uploadSpeechToTextAudioFiles(formData, params);

            showToaster(toasterTypes.SUCCESS, 'Audio file submitted successfully');

            setAudioBlob(null);
            audioChunksRef.current = [];
            setVariables([]);

            dispatch(setLoading(false));
            navigateToDataCollection();
        } catch (error) {
            console.error('Error submitting data:', error);
            showToaster(toasterTypes.ERROR, error?.message || 'Failed to submit data');
            dispatch(setLoading(false));
        }
    };

    function onValueChange(name, value, values, fieldValues = null) {
        const tempFormFields: any = cloneDeep(variables);
        const fieldIndex = tempFormFields.findIndex((field) => field.id === name);
        const originalField = tempFormFields[fieldIndex];
        let updatedValue = value;

        const updatedField = {
            ...originalField,
            field_selected_value: updatedValue,
            localized_field_selected_value: updatedValue,
            data_entry_language: selectedLanguage.value,
        };

        const updatedFormFields = [...variables];
        updatedFormFields[fieldIndex] = updatedField;
        setVariables(updatedFormFields);

    }

    function getValues(data, type) {
        const valuesData: any[] = [];
        if (type === "prev") {
            data.forEach(item => {
                valuesData.push({
                    label: item.value,
                    value: item.value
                });
            });
        } else {
            data.forEach(item => {
                valuesData.push({
                    label: item,
                    value: item
                });
            });
        }
        return valuesData;
    }

    function processFormFields(formFieldsData) {
        const processedFields = formFieldsData.map(field => {
            const shouldTransform =
                field.field_type === "dropDown" ||
                field.field_type === "radioButton" ||
                field.field_type === "checkBox";

            const needsPrev = field.field_type === "dropDownPreviousResponses";
            const type = needsPrev ? "prev" : "";

            return {
                ...field,
                field_value: (shouldTransform || needsPrev)
                    ? getValues(field.field_value || [], type)
                    : field.field_value,

                localized_field_selected_value: null,
                data_entry_language: "en",

                localized: field.localized?.map(loc => ({
                    ...loc,
                    localized_field_value: shouldTransform
                        ? getValues(loc.localized_field_value || [], "")
                        : loc.localized_field_value,

                    localized_field_row_value: shouldTransform
                        ? getValues(loc.localized_field_row_value || [], "")
                        : loc.localized_field_row_value
                })) || []
            };
        });

        return processedFields;
    }

    const processFieldValue = (field: any, value, targetLanguage) => {
        // Handle null/undefined values
        if (value === null || value === undefined) {
            return {
                englishFieldValue: null,
                localizedFieldValue: null
            };
        }

        // Handle empty arrays for grid fields
        if (!value && gridFields.includes(field.field_type)) {
            return {
                englishFieldValue: [],
                localizedFieldValue: []
            };
        }

        // Handle empty strings or other falsy values
        if (!value) {
            return {
                englishFieldValue: value,
                localizedFieldValue: value
            };
        }

        // If field is not localized, return the value as is
        if (!field.is_localized) {
            if (["dateField", "dateTime"].includes(field.field_type) && value) {
                const isoDate = dayjs(value).toISOString();
                return {
                    englishFieldValue: isoDate,
                    localizedFieldValue: isoDate
                }
            }
            return {
                englishFieldValue: value,
                localizedFieldValue: value
            };
        }

        // Handle English target language - use field.field_value directly
        if (targetLanguage === 'en') {
            // If data was entered in English, return as is
            if (field.data_entry_language === 'en') {
                return {
                    englishFieldValue: value,
                    localizedFieldValue: value
                };
            }
            // If data was entered in another language, we need to convert to English
            // This will be handled in the switch cases below with targetLocalization = null
        }

        // Find the target language localization (will be null for English)
        const targetLocalization = targetLanguage === 'en' ? null : field.localized.find(loc => loc.language === targetLanguage);

        // If target language is not English and no localization found, return original values
        if (targetLanguage !== 'en' && (!targetLocalization || !targetLocalization.localized_field_value)) {
            return {
                englishFieldValue: value,
                localizedFieldValue: value
            };
        }

        // Find the data entry language localization to get the mapping
        const dataEntryLocalization = field.localized.find(loc => loc.language === field.data_entry_language);

        let targetFieldValue = value;
        let englishFieldValue = value;

        switch (field.field_type) {
            case "radioButton": {
                // Find index based on data entry language
                let sourceIndex = -1;
                if (field.data_entry_language === 'en') {
                    sourceIndex = field.field_value.findIndex(item => item.value === value);
                } else if (dataEntryLocalization) {
                    sourceIndex = dataEntryLocalization.localized_field_value.findIndex(item => item.value === value);
                }

                if (sourceIndex !== -1) {
                    englishFieldValue = field.field_value[sourceIndex].value;
                    // If target is English, use English value, otherwise use target localization
                    targetFieldValue = targetLanguage === 'en' ?
                        field.field_value[sourceIndex].value :
                        targetLocalization.localized_field_value[sourceIndex]?.value || value;
                }
                break;
            }

            case "dropDown": {
                let sourceIndex = -1;
                if (field.data_entry_language === 'en') {
                    sourceIndex = field.field_value.findIndex(item => {
                        if (typeof value === "object") {
                            return item.label === value.label && item.value === value.value;
                        }
                        return item.value === value;
                    });
                } else if (dataEntryLocalization) {
                    sourceIndex = dataEntryLocalization.localized_field_value.findIndex(item => {
                        if (typeof value === "object") {
                            return item.label === value.label && item.value === value.value;
                        }
                        return item.value === value;
                    });
                }

                if (sourceIndex !== -1) {
                    englishFieldValue = field.field_value[sourceIndex];
                    // If target is English, use English value, otherwise use target localization
                    targetFieldValue = targetLanguage === 'en' ?
                        field.field_value[sourceIndex] :
                        targetLocalization.localized_field_value[sourceIndex] || value;
                }
                break;
            }

            case "checkBox": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach((val: any) => {
                    let sourceIndex = -1;
                    if (field.data_entry_language === 'en') {
                        sourceIndex = field.field_value.findIndex(item => item.value === val);
                    } else if (dataEntryLocalization) {
                        sourceIndex = dataEntryLocalization.localized_field_value.findIndex(item => item.value === val);
                    }

                    if (sourceIndex !== -1) {
                        englishSelected.push(field.field_value[sourceIndex].value);
                        // If target is English, use English value, otherwise use target localization
                        targetSelected.push(targetLanguage === 'en' ?
                            field.field_value[sourceIndex].value :
                            targetLocalization.localized_field_value[sourceIndex]?.value || val);
                    } else {
                        englishSelected.push(val);
                        targetSelected.push(val);
                    }
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            case "checkBoxGrid": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach(item => {
                    // Handle row mapping
                    let rowIndex = -1;
                    if (field.data_entry_language === 'en') {
                        rowIndex = field.field_row_value.findIndex(row => row === item.rowKey);
                    } else if (dataEntryLocalization) {
                        rowIndex = dataEntryLocalization.localized_field_row_value.findIndex(row => row === item.rowKey);
                    }

                    const englishRowKey = rowIndex !== -1 ? field.field_row_value[rowIndex] : item.rowKey;
                    const targetRowKey = targetLanguage === 'en' ?
                        englishRowKey :
                        (rowIndex !== -1 ? targetLocalization.localized_field_row_value[rowIndex] : item.rowKey);

                    // Handle column mapping
                    const englishCols: any = [];
                    const targetCols: any = [];

                    item.value.forEach(colVal => {
                        let colIndex = -1;
                        if (field.data_entry_language === 'en') {
                            colIndex = field.field_value.findIndex(col => col === colVal);
                        } else if (dataEntryLocalization) {
                            colIndex = dataEntryLocalization.localized_field_value.findIndex(col => col === colVal);
                        }

                        const englishCol = colIndex !== -1 ? field.field_value[colIndex] : colVal;
                        const targetCol = targetLanguage === 'en' ?
                            englishCol :
                            (colIndex !== -1 ? targetLocalization.localized_field_value[colIndex] : colVal);

                        englishCols.push(englishCol);
                        targetCols.push(targetCol);
                    });

                    englishSelected.push({ rowKey: englishRowKey, value: englishCols });
                    targetSelected.push({ rowKey: targetRowKey, value: targetCols });
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            case "radioButtonGrid": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach(item => {
                    // Handle row mapping
                    let rowIndex = -1;
                    if (field.data_entry_language === 'en') {
                        rowIndex = field.field_row_value.findIndex(row => row === item.rowKey);
                    } else if (dataEntryLocalization) {
                        rowIndex = dataEntryLocalization.localized_field_row_value.findIndex(row => row === item.rowKey);
                    }

                    // Handle column mapping
                    let colIndex = -1;
                    if (field.data_entry_language === 'en') {
                        colIndex = field.field_value.findIndex(col => col === item.value);
                    } else if (dataEntryLocalization) {
                        colIndex = dataEntryLocalization.localized_field_value.findIndex(col => col === item.value);
                    }

                    const englishRowKey = rowIndex !== -1 ? field.field_row_value[rowIndex] : item.rowKey;
                    const englishColVal = colIndex !== -1 ? field.field_value[colIndex] : item.value;

                    const targetRowKey = targetLanguage === 'en' ?
                        englishRowKey :
                        (rowIndex !== -1 ? targetLocalization.localized_field_row_value[rowIndex] : item.rowKey);
                    const targetColVal = targetLanguage === 'en' ?
                        englishColVal :
                        (colIndex !== -1 ? targetLocalization.localized_field_value[colIndex] : item.value);

                    englishSelected.push({ rowKey: englishRowKey, value: englishColVal });
                    targetSelected.push({ rowKey: targetRowKey, value: targetColVal });
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            case "textBoxGrid": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach(item => {
                    const { rowKey, colKey, value: cellValue } = item;

                    let rowIndex = -1;
                    if (field.data_entry_language === 'en') {
                        rowIndex = field.field_row_value.findIndex(row => row === rowKey);
                    } else if (dataEntryLocalization) {
                        rowIndex = dataEntryLocalization.localized_field_row_value.findIndex(row => row === rowKey);
                    }

                    let colIndex = -1;
                    if (field.data_entry_language === 'en') {
                        colIndex = field.field_value.findIndex(col => col === colKey);
                    } else if (dataEntryLocalization) {
                        colIndex = dataEntryLocalization.localized_field_value.findIndex(col => col === colKey);
                    }

                    const englishRowKey = rowIndex !== -1 ? field.field_row_value[rowIndex] : rowKey;
                    const englishColKey = colIndex !== -1 ? field.field_value[colIndex] : colKey;

                    const targetRowKey = targetLanguage === 'en' ?
                        englishRowKey :
                        (rowIndex !== -1 ? targetLocalization.localized_field_row_value[rowIndex] : rowKey);
                    const targetColKey = targetLanguage === 'en' ?
                        englishColKey :
                        (colIndex !== -1 ? targetLocalization.localized_field_value[colIndex] : colKey);

                    englishSelected.push({ rowKey: englishRowKey, colKey: englishColKey, value: cellValue });
                    targetSelected.push({ rowKey: targetRowKey, colKey: targetColKey, value: cellValue });
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            default: {
                englishFieldValue = value;
                targetFieldValue = value;
                break;
            }
        }
        return {
            englishFieldValue: englishFieldValue,
            localizedFieldValue: targetFieldValue
        };
    };

    // Get localized form name based on selected language
    const getLocalizedFormName = (form) => {
        if (selectedLanguage?.value === 'en' || !form.is_localized) {
            return form?.form_name;
        }

        const localization = form.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_value || form?.form_name;
    };

    const getLocalizedFieldName = (field) => {
        if (selectedLanguage?.value === 'en' || !field?.is_localized) {
            return field?.field_name;
        }

        const localization = field.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_value || field.field_name;
    };


    const getDisplayValue = (field) => {
        const { englishFieldValue, localizedFieldValue }: any = processFieldValue(field, field.field_selected_value, selectedLanguage.value || 'en');

        return localizedFieldValue || englishFieldValue || field.field_selected_value || "";
    };

    // Get localized field values (options) based on selected language
    const getLocalizedFieldValues = (field) => {
        if (selectedLanguage?.value === 'en' || !field.is_localized) {
            return field.field_value;
        }

        const localization = field.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_field_value || field.field_value;
    };

    // Get localized field values (options) based on selected language
    const getLocalizedFieldRowValues = (field) => {
        if (selectedLanguage?.value === 'en' || !field.is_localized) {
            return field.field_row_value;
        }
        const localization = field.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_field_row_value || field.field_row_value;
    };

    const getLanguageValues = () => {
        let temp: any = formData;
        const formLanguages = temp?.localized || [];
        const otherLanguages = languagesData.filter(
            lang =>
                lang.value !== "en" && formLanguages.some(i => i.language === lang.value)
        );
        const english = languagesData.find(lang => lang.value === "en");
        return english ? [english, ...otherLanguages] : otherLanguages;
    };

    const handleLanguageChange = (e) => {
        setSelectedLanguage(e);
    };

    const handleTranslateChange = (e) => {
        setIsTranslate(e);
    }

    const getUidField = (type = "value") => {
        const uidField = variables
            ?.flatMap(fields => fields)
            .find(field => field.field_use_type.includes("UID"));

        if (!uidField || !uidField.field_selected_value) {
            return type === "exists" ? false : "";
        }

        if (type === "exists") {
            return true;
        }

        if (type === "name") {
            return uidField.field_name || "";
        }

        const val = uidField.field_selected_value;

        if (typeof val === "object") {
            return type === "value" ? val.value || "" : "";
        }

        return val;
    };

    const navigateToDataCollection = () => {
        navigate(window.innerWidth > 750 ? "/dataCollection" : "/dataCollectionMobile", { state: props?.projectData });
    }

    const hasUnsavedData = () => {
        return (
            recording ||
            audioChunksRef.current.length > 0 ||
            variables.some(v => v.field_selected_value)
        );
    };

    const confirmExit = () => {
        if (!hasUnsavedData()) return true;

        return window.confirm(
            "You have an ongoing recording or unsaved data. Leaving this page will permanently discard it. Do you want to continue?"
        );
    };

    useEffect(() => {
        const handlePopState = () => {
            if (!hasUnsavedData()) return;

            const shouldLeave = window.confirm(
                "You have an ongoing recording or unsaved data. Leaving this page will permanently discard it. Do you want to continue?"
            );

            if (!shouldLeave) {
                window.history.pushState(null, "", window.location.href);
            } else {
                navigate(-1);
            }
        };

        if (!historyInitializedRef.current) {
            window.history.pushState(null, "", window.location.href);
            historyInitializedRef.current = true;
        }

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [recording, variables]);

    return (
        <>
            {/* <TopBar projectData={props.projectData} navigate={window.innerWidth > 750 ? "/dataCollection" : "/dataCollectionMobile"} /> */}

            <div className="speech-to-text-container">

                <div className='close-container'>
                    <i
                        className="icon icon-close"
                        onClick={async () => {
                            if (!confirmExit()) return;

                            if (recording) {
                                await stopRecording();
                            }

                            navigateToDataCollection();
                        }}
                    />
                </div>
                {/* <div className='coguide-banner-container'>
                    {formData?.banner_image ? <img src={`https://go.coguide.in/c1/download/${formData.banner_image}`} alt='preview-banner' className='banner' /> : <img src={`${process.env.PUBLIC_URL}/images/survey_tools.svg`} alt='preview-banner' className='banner' />}
                </div> */}
                <div className='coguide-logo-container'>
                    <p className="speech-title">SPEECH TO TEXT DATA ENTRY</p>
                    <img src={`${process.env.PUBLIC_URL}/images/powered_by_coguide2.png`} alt='logo' className='logo' />
                </div>
                <div className='name-container'>
                    <div className='p-0 left-container'>
                        <div className='form-name'>{getLocalizedFormName(formData)}</div>
                        <div className='form-description'>{formData?.form_desc}</div>
                        {getUidField("exists") && <div className='selected-uid'>{getUidField("name")} : {getUidField("value")}</div>}
                    </div>
                    {<div className="right-container">
                        <div className='language'>
                            <FieldItem
                                {...SurveyFormElements.language}
                                values={getLanguageValues()}
                                isMandatory={false}
                                value={selectedLanguage}
                                onChange={(...e) => handleLanguageChange(...e)}
                            />
                        </div>


                        <div className="recording-controls">
                            <div className="speech-to-text-button">
                                <div
                                    className={`microphone ${recording ? 'recording' : ''} ${!props.isCreditsAvailable ? 'disabled' : ''}`}
                                    onClick={props.isCreditsAvailable ? (recording ? stopRecording : startRecording) : undefined}
                                >
                                    {recording ? (
                                        <>
                                            <AudioMutedOutlined />
                                            <span>Stop Recording</span>
                                        </>
                                    ) : (
                                        <>
                                            <AudioOutlined />
                                            <span>{audioBlob ? 'Re-record' : 'Start Recording'}</span>
                                        </>
                                    )}
                                </div>

                                {!props.isCreditsAvailable && (
                                    <div className="no-credits-overlay">No Credits Available.</div>
                                )}
                            </div>

                            {audioBlob && !recording && (
                                <div className="audio-status">
                                    <span>✓ Audio recorded</span>
                                </div>
                            )}
                        </div>

                    </div>}
                    <div className="right-container">
                        <div className='is-translate'>
                            <FieldItem
                                {...SurveyFormElements.is_translate}
                                value={isTranslate}
                                isMandatory={false}
                                onChange={(...e) => handleTranslateChange(...e)}
                            />
                        </div>
                        {isTranslate?.value && <div className='prompt-message'>
                            <FieldItem
                                {...SurveyFormElements.prompt_message}
                                value={promptMessage}
                                onChange={(e) => {
                                    typeof e === "string" && setPromptMessage(e);
                                }}
                            />
                        </div>}
                    </div>
                </div>


                {/* Variables List */}
                <h3 className="stt-variables-to-fill">Variables to Fill</h3>
                <div className="stt-variables-list">

                    {variables.length === 0 ? (
                        <p>No variables to display</p>
                    ) : (
                        variables.map((obj, index) => (
                            <div className="field-body">
                                <FieldItem
                                    key={index}
                                    name={obj.id}
                                    placeholder={
                                        obj.field_type === "number" ||
                                            obj.field_type === "textBox" ||
                                            obj.field_type === "textArea"
                                            ? "Enter"
                                            : "Select"
                                    }
                                    isDisabled={!uidFieldIds.includes(obj.id)}
                                    label={obj.field_type === "instruction" ? "" : getLocalizedFieldName(obj)}
                                    type={
                                        obj.field_type === "fileUpload"
                                            ? FIELD_TYPES.FILE_UPLOAD
                                            : obj.field_type === "dateField"
                                                ? FIELD_TYPES.DATEPICKER_NEW
                                                : obj.field_type === "dropDown"
                                                    ? FIELD_TYPES.DROP_DOWN
                                                    : obj.field_type === "dropDownPreviousResponses"
                                                        ? FIELD_TYPES.DROP_DOWN
                                                        : obj.field_type === "boolean"
                                                            ? FIELD_TYPES.BOOLEAN
                                                            : obj.field_type === "radioButton"
                                                                ? FIELD_TYPES.RADIO_BUTTON
                                                                : obj.field_type === "checkBox"
                                                                    ? FIELD_TYPES.CHECK_BOX
                                                                    : obj.field_type === "textArea"
                                                                        ? FIELD_TYPES.TEXT_AREA
                                                                        : obj.field_type === "textBox"
                                                                            ? FIELD_TYPES.TEXT
                                                                            : obj.field_type === "time"
                                                                                ? FIELD_TYPES.TIME_PICKER
                                                                                : obj.field_type === "dateTime"
                                                                                    ? FIELD_TYPES.DATE_TIME_PICKER
                                                                                    : obj.field_type === "signatureCapture"
                                                                                        ? FIELD_TYPES.SIGNATURE_CAPTURE
                                                                                        : obj.field_type === "imageCapture"
                                                                                            ? FIELD_TYPES.IMAGE_CAPTURE
                                                                                            : obj.field_type === "videoCapture"
                                                                                                ? FIELD_TYPES.VIDEO_CAPTURE
                                                                                                : obj.field_type === "audioCapture"
                                                                                                    ? FIELD_TYPES.AUDIO_CAPTURE
                                                                                                    : obj.field_type === "instruction"
                                                                                                        ? FIELD_TYPES.INPUT_INSTRUCTION
                                                                                                        : obj.field_type === "checkBoxGrid"
                                                                                                            ? FIELD_TYPES.CHECK_BOX_GRID
                                                                                                            : obj.field_type === "radioButtonGrid"
                                                                                                                ? FIELD_TYPES.RADIO_BUTTON_GRID
                                                                                                                : obj.field_type === "textBoxGrid"
                                                                                                                    ? FIELD_TYPES.TEXT_BOX_GRID
                                                                                                                    : obj.field_type === "fileUploadOCR"
                                                                                                                        ? FIELD_TYPES.FILE_UPLOAD
                                                                                                                        : FIELD_TYPES.TEXT
                                    }
                                    isNumber={obj.field_type === "number" ? true : false}
                                    value={obj.field_type === "instruction" ? getLocalizedFieldName(obj) : getDisplayValue(obj)}
                                    values={getLocalizedFieldValues(obj)}
                                    rowValues={getLocalizedFieldRowValues(obj)}
                                    isListed={true}
                                    onChange={(...e) => {
                                        handleChange(obj.id, ...e);
                                    }}
                                />
                            </div>
                        ))
                    )}
                </div>

                {/* Submit Button */}
                {variables.length > 0 && (
                    <div className="submit-controls">
                        <CustomButton
                            text="Submit Recording"
                            type="primary"
                            onClick={handleSubmit}
                            isDisabled={!audioBlob || recording}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default SpeechToTextDataEntry;